import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { SectionCard } from "@/components/common/SectionCard";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";
import { AdsAssistantLibrarySection } from "@/components/ads-assistant/AdsAssistantLibrarySection";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

/**
 * Host component for the `adsGuidance` Manager-bucket mode. Re-homed
 * out of the Sales-bucket Ads Assistant → Library tab on 2026-06-15
 * per docs/design/admin_split_and_entitlements.md §7.6.
 *
 * Hosts the same `AdsAssistantLibrarySection` (categories, templates,
 * policies, post-tags, social mappings) that used to live as a tab
 * inside `AdsAssistantPanel`. The underlying section + its inner
 * panels stay co-located in `src/components/ads-assistant/` for this
 * slice — they're tightly coupled to the Ads-Assistant client +
 * permissions module, and moving the directory is a cosmetic cleanup
 * that can land in a follow-up.
 *
 * The Sales-bucket `adsAssistant` mode keeps the rep-facing surfaces
 * (Ingestion, Suggestion, Feed import, Org Settings).
 */
export function AdsGuidancePanel() {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();

  const canAccess = canEditAdsGuidance(auth);

  if (!canAccess) {
    return (
      <div className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Ads Guidance</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Ads guidance authoring is available to trainers, marketing, and ads-guidance editors.
        </p>
      </div>
    );
  }

  if (!scopedOrgId) {
    return (
      <div className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Ads Guidance</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Select a workspace organization first.
        </p>
      </div>
    );
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ads-guidance-surface" }} className="w-full text-left space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Ads Guidance</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">
            Categories, templates, policies, post tags, and social mappings — the authoring
            surface that the Sales-bucket Ads Assistant reads from when reps log ads.
          </p>
        </div>
      </div>

      <SectionCard
        title="Library"
        description={(
          <>
            Teach the assistant how to classify ads, recognize formats, and flag policy issues.
            Use the <span className="font-medium">?</span> hints in each column for detail.
          </>
        )}
        rightSlot={(
          <AdsHelpHint label="Library — full guide">
            <div className="space-y-2">
              <p>
                <strong>Categories</strong> become chips when logging posts. <strong>Templates</strong>{" "}
                are named formats (badge on each row). <strong>Policies</strong> add substring
                warnings the stub pass surfaces. <strong>Social mappings</strong> tie locations
                to Facebook / Google URLs for import.
              </p>
            </div>
          </AdsHelpHint>
        )}
      >
        <AdsAssistantLibrarySection scopedOrgId={scopedOrgId} embedded />
      </SectionCard>
    </div>
  );
}
